import { isClusterEntity } from "@faro-lotv/data-management";
import { GUID, walkWithQueue } from "@faro-lotv/foundation";
import { isValid } from "@faro-lotv/ielement-types";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
  isRevisionScanEntity,
  RegistrationEdgeRevision,
} from "@faro-lotv/service-wires";
import { ChildrenMap } from "../revision/revision-entity-selectors";
import { EntityMap } from "../revision/revision-slice";
import {
  getDescendantIds,
  isEntityDeletedOnRevision,
} from "../revision/selector-helpers";

/**
 * @param entityIds the entity ids to compute the affected entities for
 * @param childrenMap the map of parent entity id to its children entities
 * @param entityMap the map of entity id to entity
 * @param partOfEdges the edges representing "part of" relationships between entities
 * @returns the IDs of the affected ancestors that are impacted directly or indirectly
 *  for the current entities in the array. If there are no affected ancestors,
 *  returns the corresponding entity IDs.
 */
export function computeAffectedAncestorIds(
  entityIds: GUID[],
  childrenMap: ChildrenMap,
  entityMap: EntityMap,
  partOfEdges: RegistrationEdgeRevision[],
): GUID[] {
  // If individual entities within a cluster are selected,
  // only edit those entities instead of the whole cluster
  const affectedEntities = new Set(
    entityIds.filter((id) => {
      const descendantIds = getDescendantIds(childrenMap, id);
      const hasAffectedDescendants = descendantIds.some(
        (descendantId) =>
          descendantId !== id && entityIds.includes(descendantId),
      );
      return !hasAffectedDescendants;
    }),
  );

  // Scans connected by a PartOf edge must be edited together
  walkWithQueue([...affectedEntities], (id, append) => {
    const connectedEdges = partOfEdges.filter(
      (edge) => edge.sourceId === id || edge.targetId === id,
    );

    for (const edge of connectedEdges) {
      if (!affectedEntities.has(edge.sourceId)) {
        affectedEntities.add(edge.sourceId);
        append(edge.sourceId);
      } else if (!affectedEntities.has(edge.targetId)) {
        affectedEntities.add(edge.targetId);
        append(edge.targetId);
      }
    }
  });

  // Cluster folding: If any entity in an Orbis/SiteScape cluster is affected, affect the cluster instead
  walkWithQueue([...affectedEntities], (id, append) => {
    const entity = entityMap[id];
    if (!entity?.parentId) return;

    const parent = entityMap[entity.parentId];
    if (!parent || parent.type === CaptureTreeEntityType.root) return;

    const children = childrenMap[parent.id];
    if (!children) return;

    const isFoldableByTypeHint =
      parent.typeHint === CaptureTreeEntityTypeHint.orbisCapture ||
      parent.typeHint === CaptureTreeEntityTypeHint.siteScapeCapture;
    const isFoldableByChildType = children.some(
      (child) =>
        child.type === CaptureTreeEntityType.orbisScan ||
        child.type === CaptureTreeEntityType.orbisFlashScan ||
        child.type === CaptureTreeEntityType.siteScapeScan,
    );
    const isFoldableCluster =
      parent.type === CaptureTreeEntityType.cluster &&
      (isFoldableByTypeHint || isFoldableByChildType);
    if (!isFoldableCluster) return;

    const childIds = children.map((child) => child.id);

    if (childIds.some((childId) => affectedEntities.has(childId))) {
      // If any child of the cluster is affected, include the cluster instead
      for (const childId of childIds) {
        affectedEntities.delete(childId);
      }
      affectedEntities.add(parent.id);
      // Check the cluster as well to walk up the hierarchy
      append(parent.id);
    }
  });

  // Exclude deleted entities
  return [...affectedEntities]
    .map((id) => entityMap[id])
    .filter(isValid)
    .filter((entity) => !isEntityDeletedOnRevision(entity))
    .map((entity) => entity.id);
}

/**
 * @param entities the entities to compute the affected captures for
 * @param atomicClusterIds the IDs of the atomic clusters
 * @returns all captures that are affected directly or indirectly for the given set of entities.
 *  there is a special handling for SiteScape:
 *  A SiteScape cluster will count as a capture, while the contained individual scans do not.
 */
export function computeActionableCaptures(
  entities: CaptureTreeEntityRevision[],
  atomicClusterIds: GUID[],
): CaptureTreeEntityRevision[] {
  return entities.filter((entity) => {
    if (atomicClusterIds.includes(entity.id)) return true;

    if (isRevisionScanEntity(entity)) {
      return !atomicClusterIds.includes(entity.parentId ?? "");
    }

    return false;
  });
}

/**
 * @param entities the entities to compute the affected clusters for
 * @param atomicClusterIds the IDs of the atomic clusters
 * @returns all clusters that are impacted directly or indirectly of the entity.
 *  Special handling for SiteScape: SiteScape clusters are not included,
 *  because they are counted as capture.
 */
export function computeActionableClusters(
  entities: CaptureTreeEntityRevision[],
  atomicClusterIds: GUID[],
): CaptureTreeEntityRevision[] {
  return entities.filter(
    (entity) =>
      isClusterEntity(entity) && !atomicClusterIds.includes(entity.id),
  );
}

/**
 * @param entities the entities to compute the recursive entities for
 * @param childrenMap the map of parent entity id to its children entities
 * @param entityMap the map of entity id to entity
 * @returns all entities that are impacted directly or indirectly for the given set of entities,
 *  including the entities themselves. Excludes entities that are deleted on the revision.
 *  Special handling for SiteScape: If a SiteScape cluster is included,
 *  the contained individual scans are not included, but the cluster is included.
 */
export function computeRecursiveEntities(
  entities: GUID[],
  childrenMap: ChildrenMap,
  entityMap: EntityMap,
): CaptureTreeEntityRevision[] {
  return entities
    .flatMap((id) => getDescendantIds(childrenMap, id))
    .map((id) => entityMap[id])
    .filter((entity) => !!entity)
    .filter((entity) => !isEntityDeletedOnRevision(entity));
}
